import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserCircle, KeyRound, CreditCard, ShieldCheck, Save, Crown, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMyAccount() {
  const { admin, getAdminAxios } = useOutletContext();
  const [billing, setBilling] = useState(null);
  const [name, setName] = useState(admin?.name || "");
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);
  const [pwdErr, setPwdErr] = useState(null);
  const [profileMsg, setProfileMsg] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await getAdminAxios().get(`${API}/reseller/me/billing`);
      setBilling(data);
    } catch (e) { /* ignore */ }
  }, [getAdminAxios]);

  useEffect(() => { refresh(); }, [refresh]);

  const saveProfile = async () => {
    setSaving(true); setProfileMsg(null);
    try {
      await getAdminAxios().put(`${API}/admin/super-admins/${admin.id}`, { name });
      setProfileMsg({ ok: true, text: "Perfil actualizado" });
    } catch (e) {
      setProfileMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const changePwd = async () => {
    setPwdMsg(null); setPwdErr(null);
    if (!pwd.current || !pwd.next) { setPwdErr("Completá ambos campos"); return; }
    if (pwd.next.length < 6) { setPwdErr("Mínimo 6 caracteres"); return; }
    if (pwd.next !== pwd.confirm) { setPwdErr("Las contraseñas no coinciden"); return; }
    setPwdSaving(true);
    try {
      await getAdminAxios().post(`${API}/auth/change-password`, { current_password: pwd.current, new_password: pwd.next });
      setPwdMsg("Contraseña actualizada. La próxima vez que inicies sesión usá la nueva.");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      setPwdErr(e.response?.data?.detail || e.message);
    } finally { setPwdSaving(false); }
  };

  const statusBadge = (status) => {
    const map = {
      active: { txt: "Activo", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
      expiring: { txt: "Por vencer", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
      expired: { txt: "Vencido", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
      none: { txt: "Sin plan", cls: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
    };
    const s = map[status] || map.none;
    return <span className={`text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>{s.txt}</span>;
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="admin-my-account-page">
      <h1 className="text-2xl font-semibold text-white flex items-center gap-2 mb-1"><UserCircle className="w-6 h-6" /> Mi cuenta</h1>
      <p className="text-sm text-slate-400 mb-6">Gestioná tu perfil, contraseña y plan de suscripción.</p>

      {/* Billing card */}
      <Card className="bg-[#13112b] border-indigo-900/30 mb-6">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-400" /> Mi suscripción</CardTitle>
          <CardDescription className="text-slate-400">Información de tu plan actual.</CardDescription>
        </CardHeader>
        <CardContent>
          {!billing ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
          ) : billing.plan_slug === "root" ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3">
              <Crown className="w-6 h-6 text-amber-400" />
              <div>
                <p className="text-white font-medium">Root Super Admin</p>
                <p className="text-xs text-slate-400">Acceso ilimitado, sin suscripción.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-white/5 rounded px-4 py-2 border border-white/10">
                <Label className="text-sm text-slate-300">Plan</Label>
                <span className="text-white font-medium" data-testid="my-plan-name">{billing.plan_name || "—"}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded px-4 py-2 border border-white/10">
                <Label className="text-sm text-slate-300">Estado</Label>
                <span data-testid="my-plan-status">{statusBadge(billing.status)}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded px-4 py-2 border border-white/10">
                <Label className="text-sm text-slate-300">Vence el</Label>
                <span className="text-white" data-testid="my-plan-expires">{billing.expires_on ? billing.expires_on.slice(0, 10) : "—"}</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 rounded px-4 py-2 border border-white/10">
                <Label className="text-sm text-slate-300">Clientes incluidos</Label>
                <span className="text-white" data-testid="my-plan-max-clients">{billing.max_clients ?? "—"}</span>
              </div>
              {billing.status === "expiring" && billing.days_left != null && (
                <div className="mt-3 flex items-start gap-2 text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded p-3" data-testid="my-billing-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Tu plan vence en {billing.days_left} {billing.days_left === 1 ? "día" : "días"}. Renová desde la sección <strong>Facturación</strong>.</span>
                </div>
              )}
              {billing.status === "expired" && (
                <div className="mt-3 flex items-start gap-2 text-red-300 text-xs bg-red-500/10 border border-red-500/30 rounded p-3" data-testid="my-billing-expired">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Tu plan venció. No vas a poder crear ni editar clientes hasta renovar.</span>
                </div>
              )}
              {billing.status === "none" && (
                <div className="mt-3 flex items-start gap-2 text-amber-300 text-xs bg-amber-500/10 border border-amber-500/30 rounded p-3">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>No tenés un plan activo. Andá a <strong>Facturación</strong> y elegí uno para empezar a operar.</span>
                </div>
              )}
              <Button className="mt-3 bg-amber-500 hover:bg-amber-600 text-black" onClick={() => (window.location.href = "/admin/billing")} data-testid="goto-billing-btn">
                {billing.status === "active" ? "Ver / renovar plan" : "Pagar / renovar ahora"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile card */}
      <Card className="bg-[#13112b] border-indigo-900/30 mb-6">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Perfil</CardTitle>
          <CardDescription className="text-slate-400">Tu nombre y email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input value={admin?.email || ""} disabled className="bg-white/5 border-white/10 text-slate-400" data-testid="my-email" />
          </div>
          <div>
            <Label className="text-slate-300">Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="my-name" />
          </div>
          {profileMsg && (
            <p className={`text-xs ${profileMsg.ok ? "text-emerald-300" : "text-red-300"}`}>{profileMsg.ok ? "✓ " : "⚠️ "}{profileMsg.text}</p>
          )}
          <Button onClick={saveProfile} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="my-save-profile">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar perfil
          </Button>
        </CardContent>
      </Card>

      {/* Password card */}
      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-400" /> Cambiar contraseña</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-slate-300">Contraseña actual</Label>
            <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="my-pwd-current" />
          </div>
          <div>
            <Label className="text-slate-300">Nueva contraseña (mín. 6)</Label>
            <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="my-pwd-new" />
          </div>
          <div>
            <Label className="text-slate-300">Confirmar nueva contraseña</Label>
            <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="my-pwd-confirm" />
          </div>
          {pwdErr && <p className="text-xs text-red-300" data-testid="my-pwd-error">⚠️ {pwdErr}</p>}
          {pwdMsg && <p className="text-xs text-emerald-300" data-testid="my-pwd-ok">✓ {pwdMsg}</p>}
          <Button onClick={changePwd} disabled={pwdSaving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="my-pwd-submit">
            {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Cambiar contraseña
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
